import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  TenantTeam,
  deactivateTeamMember,
  fetchTenantTeam,
  inviteLink,
  inviteTeamMember,
  setTeamMemberRole,
} from '../../api/tenantAdmin';
import './TenantTeamPage.css';

/**
 * Who at the partner can use the portal.
 *
 * Inviting does not create an account. It records that an address is
 * expected and produces a link; the person signs in with Google or a
 * password of their own choosing and the backend binds them. We never hold
 * their password, and a link that is never opened grants nothing.
 */

const TenantTeamPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [team, setTeam] = useState<TenantTeam>({ members: [], invites: [] });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [freshLink, setFreshLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const data = await fetchTenantTeam(token);
      // Normalised at the boundary rather than at each use. Either list
      // being absent would otherwise crash the admin count below as well as
      // the table, and losing the whole screen to a malformed payload is a
      // worse failure than showing an empty one.
      setTeam({ members: data?.members ?? [], invites: data?.invites ?? [] });
    } catch (e: any) {
      setError(e?.message || 'Could not load your team.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn: (token: string) => Promise<any>, after?: () => void) => {
    if (!currentUser) return;
    setBusy(true);
    setError('');
    try {
      await fn(await currentUser.getIdToken());
      after?.();
      await load();
    } catch (e: any) {
      setError(e?.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    setFreshLink('');
    setCopied(false);
    act(
      async token => {
        const created = await inviteTeamMember(token, email.trim(), role);
        setFreshLink(inviteLink(created.token));
        setEmail('');
      }
    );
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(freshLink);
      setCopied(true);
    } catch {
      // Clipboard is blocked in some browsers. The link is on screen and
      // selectable, so this is a convenience, not the mechanism.
      setCopied(false);
    }
  };

  if (loading) return <div className="tenant-team"><p>Loading…</p></div>;

  const activeAdmins = team.members.filter(m => m.active && m.role === 'admin').length;

  return (
    <div className="tenant-team">
      <h2>Your team</h2>
      <p className="tenant-team-intro">
        Everyone here can see your cases and download finished documents. Admins can
        also change your branding and invite colleagues.
      </p>

      {error && <div className="tenant-team-error" role="alert">{error}</div>}

      <form className="tenant-invite-form" onSubmit={invite}>
        <input
          type="email"
          required
          value={email}
          placeholder="colleague@yourcompany.com"
          aria-label="Email address to invite"
          onChange={e => setEmail(e.target.value)}
        />
        <select
          value={role}
          aria-label="Role for the invited person"
          onChange={e => setRole(e.target.value as 'admin' | 'member')}
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
        <button type="submit" disabled={busy}>Create invite</button>
      </form>

      {freshLink && (
        <div className="tenant-invite-link" role="status">
          <p>Send this link to them. It works once and expires in 14 days.</p>
          <code>{freshLink}</code>
          <button type="button" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
        </div>
      )}

      <table className="tenant-team-table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Role</th>
            <th>Status</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {team.members.map(member => (
            <tr key={member.id} className={member.active ? '' : 'inactive'}>
              <td>{member.email}</td>
              <td>
                <select
                  value={member.role}
                  aria-label={`Role for ${member.email}`}
                  disabled={busy || !member.active}
                  onChange={e =>
                    act(token =>
                      setTeamMemberRole(token, member.id, e.target.value as 'admin' | 'member')
                    )
                  }
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td>
                {!member.active
                  ? 'Removed'
                  : member.accepted
                  ? 'Active'
                  : 'Invited, not signed in yet'}
              </td>
              <td>
                {member.active && (
                  <button
                    type="button"
                    className="tenant-team-remove"
                    disabled={busy}
                    onClick={() => act(token => deactivateTeamMember(token, member.id))}
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
          {team.invites.map(pending => (
            <tr key={pending.id} className="pending">
              <td>{pending.email}</td>
              <td>{pending.role}</td>
              <td>Invite sent, not accepted</td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>

      {activeAdmins === 1 && (
        <p className="tenant-team-note">
          You are the only admin. Make someone else an admin before removing yourself,
          or nobody will be able to change your settings.
        </p>
      )}
    </div>
  );
};

export default TenantTeamPage;
